import * as React from 'react'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'

import { cn } from '@/lib/utils'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, children, ...props }, ref) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const tableRef = React.useRef<HTMLTableElement | null>(null)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] =
    React.useState(false)

  const measureOverflow = React.useCallback(() => {
    const container = containerRef.current
    const table = tableRef.current

    if (!container || !table) {
      return
    }

    const nextHasOverflow = table.scrollWidth > container.clientWidth + 1

    setHasHorizontalOverflow((current) =>
      current === nextHasOverflow ? current : nextHasOverflow
    )
  }, [])

  const setTableRef = React.useCallback(
    (node: HTMLTableElement | null) => {
      tableRef.current = node

      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref]
  )

  React.useLayoutEffect(() => {
    measureOverflow()
  }, [children, className, measureOverflow])

  React.useEffect(() => {
    const container = containerRef.current
    const table = tableRef.current

    if (!container || !table) {
      return
    }

    const handleResize = () => {
      measureOverflow()
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)
    resizeObserver.observe(table)

    const mutationObserver = new MutationObserver(handleResize)
    mutationObserver.observe(table, {
      childList: true,
      characterData: true,
      subtree: true
    })

    window.addEventListener('resize', handleResize)

    const animationFrame = window.requestAnimationFrame(handleResize)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener('resize', handleResize)
      mutationObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [measureOverflow])

  const tableElement = (
    <table
      ref={setTableRef}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    >
      {children}
    </table>
  )

  return (
    <div ref={containerRef} className='border-input relative w-full'>
      {hasHorizontalOverflow ? (
        <SimpleBar autoHide={false} className='w-full'>
          {tableElement}
        </SimpleBar>
      ) : (
        tableElement
      )}
    </div>
  )
})
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-input [&_tr]:border-b', className)}
    {...props}
  />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('border-input [&_tr:last-child]:border-0', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'bg-muted/50 border-input border-t font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'data-[state=selected]:bg-muted hover:bg-muted/50 border-input border-b transition-colors',
      className
    )}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-muted-foreground border-input h-12 px-4 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'border-input p-4 align-middle [&:has([role=checkbox])]:pr-0',
      className
    )}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground border-input mt-4 text-sm', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption
}
